// apps/web/src/tour/tourTypes.ts
import type { CustomerRecord } from '@/domain/types';
import type { TourBaseline } from '@/state/tourStore';

/** Everything a step predicate is allowed to read. `el` is injectable so predicates are unit-testable. */
export interface TourCtx {
  customer: CustomerRecord | undefined;
  baseline: TourBaseline;
  el: (selector: string) => Element | null;
}

export interface TourStep {
  id: string;
  /** Coach-line copy shown for this step. */
  coach: string;
  /** Primary target to ring (strong). CSS selector. */
  anchor: string;
  /** Optional second target, ringed faintly (the step-10 fork). */
  secondaryAnchor?: string;
  /** Scroll the primary anchor into view when this step becomes active. */
  autoScroll?: boolean;
  /** True once this step's action has happened. Prefer durable signals. */
  done: (ctx: TourCtx) => boolean;
}

export interface Tour {
  id: string;
  steps: TourStep[];
  /** Terminal alternative completion (e.g. lead pushed to the agent queue ⇒ handoff). */
  endedEarly?: (ctx: TourCtx) => boolean;
}

export type ResolveResult =
  | { kind: 'active'; step: TourStep }
  | { kind: 'complete' }
  | { kind: 'handoff' };

/**
 * Self-syncing, non-blocking: ring the step AFTER the highest-done step. A gap
 * (an earlier step left undone while a later one is done) is skipped, so the user
 * is never trapped — the ring follows wherever they actually are.
 */
export function resolveActiveStep(tour: Tour, ctx: TourCtx): ResolveResult {
  if (tour.endedEarly?.(ctx)) return { kind: 'handoff' };
  let last = -1;
  tour.steps.forEach((s, i) => {
    if (s.done(ctx)) last = i;
  });
  if (last === tour.steps.length - 1) return { kind: 'complete' };
  // safe: the complete-branch above guarantees last < steps.length - 1, so last + 1 is in range
  return { kind: 'active', step: tour.steps[last + 1]! };
}
